import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Delivery } from "../lib/api";
import { fmtDate } from "../lib/auditFormat";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  FileBox, CheckCircle2, AlertTriangle, ArrowRight, Truck, ChevronRight,
  Clock, Sparkles, XCircle, SlidersHorizontal, CalendarClock,
} from "lucide-react";
import { cn } from "../lib/utils";
import ExportButton from "../components/ExportButton";
import { defaultFilters, buildApiParams } from "../lib/filters";

// ──────────────── KPI Card ────────────────
function KpiCard({ tone, Icon, label, value, subtext, active, onClick, testid }) {
  const tones = {
    blue: {
      bg: "bg-blue-50/70", ring: "ring-blue-200",
      icon: "bg-blue-100 text-blue-700", activeRing: "ring-blue-400",
    },
    emerald: {
      bg: "bg-emerald-50/70", ring: "ring-emerald-200",
      icon: "bg-emerald-100 text-emerald-700", activeRing: "ring-emerald-400",
    },
    amber: {
      bg: "bg-amber-50/70", ring: "ring-amber-200",
      icon: "bg-amber-100 text-amber-700", activeRing: "ring-amber-400",
    },
    green: {
      bg: "bg-green-50/70", ring: "ring-green-200",
      icon: "bg-green-100 text-green-700", activeRing: "ring-green-400",
    },
    purple: {
      // Positive KPI — brighter emphasis.
      bg: "bg-gradient-to-br from-purple-50 to-fuchsia-50",
      ring: "ring-purple-300",
      icon: "bg-purple-100 text-purple-700",
      activeRing: "ring-purple-500",
    },
    red: {
      bg: "bg-red-50/70", ring: "ring-red-200",
      icon: "bg-red-100 text-red-700", activeRing: "ring-red-400",
    },
  }[tone] || {};
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn(
        "group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md sm:gap-3 sm:px-4 sm:py-3",
        tones.bg, tones.ring,
        active && `ring-2 ${tones.activeRing} shadow-sm`,
      )}>
      <span className={cn(
        "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10",
        tones.icon,
      )}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-600 sm:text-[10px] sm:tracking-[0.18em]">
          {label}
        </div>
        <div className="mt-0.5 font-display text-2xl font-bold leading-none tracking-tight text-slate-900 sm:text-3xl">
          {value}
        </div>
        <div className="mt-1 hidden text-[11px] text-slate-500 sm:block">{subtext}</div>
      </div>
      <ChevronRight className="hidden h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 sm:block" />
    </button>
  );
}

function ToolbarSelect({ value, onChange, placeholder, options, testid, width = "w-[180px]" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("h-9 text-xs", width)} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const STATUS_TABS = [
  { value: "all",              label: "All" },
  { value: "due_today",        label: "Due Today" },
  { value: "ready",            label: "Ready" },
  { value: "pending",          label: "Pending" },
  { value: "delivered",        label: "Delivered" },
  { value: "early_delivered",  label: "Early Delivered" },
  { value: "overdue",          label: "Overdue" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all",             label: "All Statuses" },
  { value: "due_today",       label: "Due Today" },
  { value: "ready",           label: "Ready" },
  { value: "pending",         label: "Pending" },
  { value: "delivered",       label: "Delivered" },
  { value: "early_delivered", label: "Early Delivered" },
  { value: "overdue",         label: "Overdue" },
];

export default function DeliveryFilesQueue() {
  const { activeUser, activeLocationIds, locations, users } = useApp();
  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  // Full unfiltered dataset used for KPI cards.
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // Read initial tab from URL (?tab=due_today) so the Dashboard shortcut lands
  // us on the right filter. Fall back to "all" for direct navigation.
  const initialTab = searchParams.get("tab") || "all";
  const [statusTab, setStatusTab] = useState(
    STATUS_FILTER_OPTIONS.some((o) => o.value === initialTab) ? initialTab : "all",
  );
  const [locFilter, setLocFilter] = useState("all");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters] = useState(defaultFilters());

  // Keep the URL in sync when the user switches tabs so links stay stable.
  useEffect(() => {
    const current = searchParams.get("tab") || "all";
    if (current === statusTab) return;
    const next = new URLSearchParams(searchParams);
    if (statusTab === "all") next.delete("tab"); else next.set("tab", statusTab);
    setSearchParams(next, { replace: true });
  }, [statusTab]);

  const isDse = activeUser?.role === "DSE";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {
          location_ids: locFilter === "all" ? locParam : locFilter,
          // Do NOT pass `status` — we always fetch the full set so counters
          // stay accurate and the client applies the active tab filter below.
          ...buildApiParams(filters),
        };
        if (isDse && activeUser?.id) params.dse_id = activeUser.id;
        if (search) params.search = search;
        const list = await Delivery.list(params);
        if (cancelled) return;
        setAllRows(list || []);
      } catch {
        if (!cancelled) setAllRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [locParam, locFilter, search, isDse, activeUser, filters]);

  const counts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const all = allRows.length;
    const ready = allRows.filter((r) => r.delivery_status === "ready").length;
    const overdue = allRows.filter((r) => r.delivery_status === "overdue").length;
    const pendingDocs = allRows.filter((r) => r.delivery_status === "pending_documents").length;
    const delivered = allRows.filter((r) => r.status === "delivered").length;
    const earlyDelivered = allRows.filter((r) => r.delivery_status === "early_delivered").length;
    // Pending Deliveries = all not-yet-delivered (Ready + Pending Documents + Overdue).
    const pending = ready + overdue + pendingDocs;
    // Due Today = promised today and not yet delivered.
    const dueToday = allRows.filter(
      (r) => r.promised_delivery_date === today && r.status !== "delivered",
    ).length;
    return { all, ready, pending, delivered, earlyDelivered, overdue, dueToday };
  }, [allRows]);

  // Apply active tab filter on client — keeps a single source of truth
  // and avoids network round-trips when the user toggles tabs.
  const rows = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (statusTab === "all") return allRows;
    if (statusTab === "due_today") return allRows.filter(
      (r) => r.promised_delivery_date === today && r.status !== "delivered",
    );
    if (statusTab === "ready") return allRows.filter((r) => r.delivery_status === "ready");
    if (statusTab === "pending") return allRows.filter((r) => r.status !== "delivered");
    if (statusTab === "delivered") return allRows.filter((r) => r.status === "delivered");
    if (statusTab === "early_delivered") return allRows.filter((r) => r.delivery_status === "early_delivered");
    if (statusTab === "overdue") return allRows.filter((r) => r.delivery_status === "overdue");
    return allRows;
  }, [allRows, statusTab]);

  const visibleLocs = locations.filter((l) => activeLocationIds.includes(l.id));

  const resetFilters = () => {
    setStatusTab("all"); setLocFilter("all"); setSearch("");
  };

  return (
    <div className="space-y-3" data-testid="delivery-files-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Branch Operations</div>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900">Delivery Performance</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Live view of every delivery file · promised vs. actual · overdue and early-delivered performance at a glance.
        </p>
      </div>

      {/* KPI cards — 2 cols on mobile, 3 on tablet, 7 on desktop */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-7 sm:gap-3">
        <KpiCard tone="blue"    Icon={FileBox}      label="All Files"          value={counts.all}
          subtext="All visible files"
          active={statusTab === "all"}
          onClick={() => setStatusTab("all")}
          testid="metric-df-all" />
        <KpiCard tone="amber"   Icon={CalendarClock} label="Due Today"          value={counts.dueToday}
          subtext="Promised today · not yet delivered"
          active={statusTab === "due_today"}
          onClick={() => setStatusTab("due_today")}
          testid="metric-df-due-today" />
        <KpiCard tone="emerald" Icon={CheckCircle2} label="Ready to Deliver"   value={counts.ready}
          subtext="Docs complete · handover pending"
          active={statusTab === "ready"}
          onClick={() => setStatusTab("ready")}
          testid="metric-df-ready" />
        <KpiCard tone="amber"   Icon={Clock}        label="Pending Deliveries" value={counts.pending}
          subtext="Includes ready + docs + overdue"
          active={statusTab === "pending"}
          onClick={() => setStatusTab("pending")}
          testid="metric-df-pending" />
        <KpiCard tone="green"   Icon={Truck}        label="Delivered"          value={counts.delivered}
          subtext="Handover complete (incl. early)"
          active={statusTab === "delivered"}
          onClick={() => setStatusTab("delivered")}
          testid="metric-df-delivered" />
        <KpiCard tone="purple"  Icon={Sparkles}     label="Early Delivered"    value={counts.earlyDelivered}
          subtext="Delivered before promise"
          active={statusTab === "early_delivered"}
          onClick={() => setStatusTab("early_delivered")}
          testid="metric-df-early" />
        <KpiCard tone="red"     Icon={XCircle}      label="Overdue"            value={counts.overdue}
          subtext="Promise date crossed"
          active={statusTab === "overdue"}
          onClick={() => setStatusTab("overdue")}
          testid="metric-df-overdue" />
      </div>

      {/* Filter toolbar — compact on mobile (collapsible), single-row on desktop */}
      <Card className="border-slate-200 px-2.5 py-2 sm:px-3">
        {/* Mobile row: search + filter toggle + export */}
        <div className="flex items-center gap-2 sm:hidden">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 flex-1 text-xs"
            data-testid="df-search-mobile"
          />
          <button
            type="button"
            onClick={() => setShowMobileFilters((v) => !v)}
            className={cn(
              "inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border text-slate-600",
              showMobileFilters
                ? "border-blue-300 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white hover:bg-slate-50",
            )}
            data-testid="df-mobile-filter-toggle"
            aria-label="Toggle filters"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          <ExportButton
            endpoint="sales"
            params={{
              location_ids: locFilter === "all" ? locParam : locFilter,
              status: statusTab === "ready" ? "ready_for_delivery"
                    : statusTab === "delivered" ? "delivered"
                    : statusTab === "pending" ? "in_progress" : undefined,
              ...(isDse && activeUser?.id ? { dse_id: activeUser.id } : {}),
              ...buildApiParams(filters),
            }}
            prefix={`Delivery_${statusTab}`}
            iconOnly
          />
        </div>
        {showMobileFilters && (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:hidden">
            <ToolbarSelect value={locFilter} onChange={setLocFilter}
              placeholder="All Locations"
              options={[
                { value: "all", label: "All Locations" },
                ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
              ]}
              testid="df-loc-filter-mobile"
              width="w-full" />
            <ToolbarSelect value={statusTab} onChange={setStatusTab}
              placeholder="Delivery Status"
              options={STATUS_FILTER_OPTIONS}
              width="w-full"
              testid="df-status-filter-mobile" />
            <button type="button" onClick={resetFilters}
              className="col-span-2 inline-flex h-8 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="df-reset-mobile"
            >
              Reset filters
            </button>
          </div>
        )}

        {/* Desktop row */}
        <div className="hidden flex-wrap items-center gap-2 sm:flex">
          <Input
            placeholder="Search SR / Customer / Mobile / VIN / Regn"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-[220px] flex-[3]"
            data-testid="df-search"
          />
          <ToolbarSelect value={locFilter} onChange={setLocFilter}
            placeholder="All Locations"
            options={[
              { value: "all", label: "All Locations" },
              ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
            ]}
            testid="df-loc-filter" />
          <ToolbarSelect value={statusTab} onChange={setStatusTab}
            placeholder="Delivery Status"
            options={STATUS_FILTER_OPTIONS}
            width="w-[170px]"
            testid="df-status-filter" />
          <button type="button" onClick={resetFilters}
            className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="df-reset"
          >
            Reset
          </button>
          <div className="ml-auto">
            <ExportButton
              endpoint="sales"
              params={{
                location_ids: locFilter === "all" ? locParam : locFilter,
                status: statusTab === "ready" ? "ready_for_delivery"
                      : statusTab === "delivered" ? "delivered"
                      : statusTab === "pending" ? "in_progress" : undefined,
                ...(isDse && activeUser?.id ? { dse_id: activeUser.id } : {}),
                ...buildApiParams(filters),
              }}
              prefix={`Delivery_${statusTab}`}
            />
          </div>
        </div>
      </Card>

      {/* Status tabs — horizontally scrollable on tiny screens if needed */}
      <div className="border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-1">
          {STATUS_TABS.map((t) => {
            const isActive = statusTab === t.value;
            return (
              <button key={t.value} type="button" onClick={() => setStatusTab(t.value)}
                className={cn(
                  "relative -mb-px whitespace-nowrap px-3 py-2 text-xs font-semibold transition-colors sm:px-3.5 sm:text-sm",
                  isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-800",
                )}
                data-testid={`df-tab-${t.value}`}
              >
                {t.label}
                {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />}
              </button>
            );
          })}
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            {isDse
              ? "No customer files yet for your account."
              : "No sales match the current filters."}
          </div>
        ) : (
          <>
            {/* Desktop / tablet table */}
            <div className="hidden md:block">
              <table className="w-full table-fixed divide-y divide-border text-sm">
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "13%" }} />
                  <col />
                </colgroup>
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Vehicle / Location</th>
                    <th className="px-3 py-2">Regn No</th>
                    <th className="px-3 py-2">Promised Date</th>
                    <th className="px-3 py-2">Actual Date</th>
                    <th className="px-3 py-2">DSE</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-white">
                  {rows.map((r) => {
                    const dse = userMap[r.dse_id];
                    const loc = locMap[r.location_id];
                    return (
                      <tr key={r.id} className="h-[62px] hover:bg-slate-50 transition-colors"
                        data-testid={`df-row-${r.id}`}>
                        <td className="px-3 py-1.5">
                          <div className="truncate text-sm font-semibold text-slate-900 leading-tight">
                            {r.customer?.name || "—"}
                          </div>
                          <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                            {r.customer?.phone || "—"}
                          </div>
                          <div className="truncate font-mono-data text-[10px] font-bold text-slate-700 leading-tight">
                            {r.sr_number || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="truncate text-sm text-slate-900 leading-tight">
                            {r.vehicle?.model || "—"}
                          </div>
                          <div className="truncate font-mono-data text-[11px] text-slate-500 leading-tight">
                            {loc ? `${loc.name} (${loc.code})` : "—"}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                          {r.registration?.registration_number || "—"}
                        </td>
                        <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                          {r.promised_delivery_date ? fmtDate(r.promised_delivery_date) : "—"}
                        </td>
                        <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                          {r.actual_delivery_date ? fmtDate(r.actual_delivery_date) : "—"}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="truncate text-xs font-medium text-slate-800"
                                data-testid={`df-dse-${r.id}`}>
                            {dse?.name || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5"><DfStatusPill row={r} /></td>
                        <td className="px-3 py-1.5 text-right">
                          <Link
                            to={`/delivery-files/${r.id}`}
                            className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                            data-testid={`df-open-${r.id}`}
                          >
                            Open <ArrowRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile — compact stacked rows */}
            <div className="md:hidden divide-y divide-slate-100">
              {rows.map((r) => {
                const loc = locMap[r.location_id];
                return (
                  <div key={r.id} className="p-3" data-testid={`df-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900">
                          {r.customer?.name || "—"}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">
                          {r.vehicle?.model || "—"}
                          {loc ? ` · ${loc.code}` : ""}
                        </div>
                        <div className="mt-1 font-mono-data text-[10px] font-bold text-slate-700">
                          {r.sr_number || "—"}
                        </div>
                      </div>
                      <DfStatusPill row={r} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-slate-600">
                        <span className="font-semibold text-slate-500">Promised:</span>{" "}
                        <span className="font-mono-data">
                          {r.promised_delivery_date ? fmtDate(r.promised_delivery_date) : "—"}
                        </span>
                      </div>
                      <Link
                        to={`/delivery-files/${r.id}`}
                        className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                        data-testid={`df-open-m-${r.id}`}
                      >
                        Open <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function DfStatusPill({ row }) {
  const s = row.delivery_status;
  if (s === "early_delivered") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-300 bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800"
        data-testid="df-status-early">
        <Sparkles className="h-3 w-3" /> Early Delivered
      </span>
    );
  }
  if (s === "delivered") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-800"
        data-testid="df-status-delivered">
        <Truck className="h-3 w-3" /> Delivered
      </span>
    );
  }
  if (s === "ready") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800"
        data-testid="df-status-ready">
        <CheckCircle2 className="h-3 w-3" /> Ready to Deliver
      </span>
    );
  }
  if (s === "overdue") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-800"
        data-testid="df-status-overdue">
        <XCircle className="h-3 w-3" /> Overdue
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800"
      data-testid="df-status-pending">
      <AlertTriangle className="h-3 w-3" /> Pending Documents
    </span>
  );
}
